// 全局错误边界：捕获 React 渲染层异常，防止白屏
// - 3D 场景崩溃、AI 请求异常、组件渲染错误等都会被捕获
// - 显示友好降级 UI + 重试按钮
import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] 捕获到未处理异常:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="h-screen w-screen flex items-center justify-center bg-bg-primary text-text-primary">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-danger/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-semibold mb-2">应用遇到问题</h2>
            <p className="text-text-secondary text-sm mb-2">
              页面渲染时发生了异常。可以尝试重新加载，或返回上一页。
            </p>
            {this.state.error && (
              <details className="text-left mt-4 bg-bg-tertiary rounded-lg p-3 text-xs">
                <summary className="cursor-pointer text-text-muted mb-2">错误详情</summary>
                <pre className="whitespace-pre-wrap break-all text-danger">
                  {this.state.error.message}
                </pre>
                {this.state.errorInfo?.componentStack && (
                  <pre className="whitespace-pre-wrap break-all text-text-muted mt-2 text-[10px]">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}
            <div className="flex gap-3 justify-center mt-6">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm"
              >
                重试
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-bg-tertiary text-text-secondary rounded-lg hover:bg-bg-secondary transition-colors text-sm"
              >
                刷新页面
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
